import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores';
import { getAuthClient } from '../utils/auth';

const auth = getAuthClient();

/**
 * Custom hook to handle all app authentication logic.
 * This includes:
 * - Initial auth check (logged in or guest auth)
 * - Login flow with OAuth code handling
 * - Logout when login not allowed
 *
 * @param {Object} options - Configuration options
 * @param {string} options.platform - The platform type (yt, siteSee, page, etc.)
 * @param {boolean} options.isPublic - Whether this is a public instance
 * @param {boolean} options.allowLogin - Whether login is allowed (from feature flags)
 * @param {Object} options.history - React Router history object for navigation
 * @returns {Object} Auth state - { isAuthed, isLoggedIn, isLoggingIn }
 */
const useAppAuth = ({ platform, isPublic, allowLogin, history }) => {
  // Track whether initial auth check has completed
  const authCheckComplete = useRef(false);
  // Track whether we've already handled an OAuth code cleanup for public pages
  const oauthCodeHandled = useRef(false);
  // Track whether we're currently processing an OAuth code (prevent duplicate attempts)
  const processingCode = useRef(false);
  // Track whether a login attempt has failed (prevent infinite redirect loop)
  const loginAttemptFailed = useRef(false);
  // Track whether preLogin redirect is in progress (prevent multiple calls)
  const preLoginInProgress = useRef(false);

  // Auth store
  const isAuthed = useAuthStore((state) => state.isAuthed);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const isLoggingIn = useAuthStore((state) => state.isLoggingIn);
  const setIsAuthed = useAuthStore((state) => state.setIsAuthed);
  const setIsLoggedIn = useAuthStore((state) => state.setIsLoggedIn);
  const setIsLoggingIn = useAuthStore((state) => state.setIsLoggingIn);
  const setSalespersonId = useAuthStore((state) => state.setSalespersonId);
  const setSalespersonNid = useAuthStore((state) => state.setSalespersonNid);
  const loginSuccess = useAuthStore((state) => state.loginSuccess);
  const logout = useAuthStore((state) => state.logout);

  // Initial auth check - try logged in first, then fall back to guest auth
  useEffect(() => {
    if (!platform) return;

    // Always check if user is already logged in first
    // This handles both fresh visits and returns from OAuth
    auth.isLoggedIn()
      .then((data) => {
        if (data) {
          // Check if this is a real user token vs guest/API token
          // Drupal's simple_oauth_extend adds is_api=true for anonymous users
          const isRealUserToken = data.user_uuid && !data.is_api;

          if (!isPublic && isRealUserToken) {
            setIsLoggedIn(true);
            setSalespersonId(data.user_uuid);
            setSalespersonNid(data.user_nid);
          }
          setIsAuthed(true);
          authCheckComplete.current = true;
        } else {
          auth.noLoginAuth()
            .then((data2) => {
              setIsAuthed(!!data2);
              authCheckComplete.current = true;
            })
            .catch(() => {
              setIsAuthed(false);
              authCheckComplete.current = true;
            });
        }
      })
      .catch(() => {
        auth.noLoginAuth()
          .then((data3) => {
            setIsAuthed(!!data3);
            authCheckComplete.current = true;
          })
          .catch(() => {
            setIsAuthed(false);
            authCheckComplete.current = true;
          });
      });
  }, [isPublic, platform, setIsAuthed, setIsLoggedIn, setSalespersonId, setSalespersonNid]);

  // Platforms that use guest auth and should never trigger OAuth login redirects
  const PUBLIC_PLATFORMS = ['page', 'siteSee', 'kiosk', 'mapOnly', 'mapTiles'];

  // Capture OAuth params from URL once at mount (before any URL manipulation)
  // Use a ref to preserve across re-renders and prevent re-reading from potentially changed URL
  const oauthParamsRef = useRef(null);
  if (oauthParamsRef.current === null) {
    const urlParams = new URLSearchParams(window.location.search);
    oauthParamsRef.current = {
      code: urlParams.get('code'),
      state: urlParams.get('state'),
      hasCode: urlParams.has('code'),
    };
  }
  const { code: oauthCode, state: oauthState, hasCode: hasOAuthCode } = oauthParamsRef.current;

  // Login flow - handle OAuth code or trigger pre-login
  useEffect(() => {
    if (!platform) return;

    // If there's an OAuth code in the URL, we're in the middle of a login callback.
    // Only process it if we're on the YT platform (not public pages).
    // Public pages should never have ended up in OAuth flow.
    if (hasOAuthCode && !oauthCodeHandled.current) {
      // If this is a public platform, just remove the code from URL and continue
      if (isPublic || PUBLIC_PLATFORMS.includes(platform)) {
        oauthCodeHandled.current = true;
        history.push('/');
        return;
      }
    }

    // Don't run login flow until initial auth check completes
    // This prevents premature preLogin() calls during the race between effects
    if (!authCheckComplete.current && !isAuthed) {
      return;
    }

    // Public pages (page, siteSee, kiosk, etc.) should never trigger login redirects.
    // They use guest auth and don't require user login.
    // Check both isPublic flag AND platform to be safe against timing issues.
    if (isPublic || PUBLIC_PLATFORMS.includes(platform)) {
      return;
    }

    // Already logging in, don't start another flow
    if (isLoggingIn) {
      return;
    }

    // For YT platform, login is required. Other platforms use feature flags.
    // If allowLogin is undefined (feature flags not loaded yet) and platform is YT,
    // we must redirect to login.
    const isYtPlatform = platform === 'yt';
    const effectiveAllowLogin = isYtPlatform || (allowLogin === true);

    // If not authed yet, wait for auth to complete
    if (!isAuthed) {
      return;
    }

    // If login is not allowed/required, just logout and stay as guest
    if (!effectiveAllowLogin) {
      logout();
      return;
    }

    // Check if we have an OAuth code to process - this takes priority over isLoggedIn
    // because the "logged in" state might just be from guest auth (noLoginAuth token)
    // Use the captured params from mount time to avoid URL timing issues

    // If we have a code, we MUST process it regardless of isLoggedIn state
    // because we're in the middle of an OAuth flow
    if (!oauthCode && isLoggedIn) {
      // No code and already logged in - nothing to do
      return;
    }

    if (oauthCode) {
      // We have an OAuth code - process it (but only once)
      // Check processingCode (in progress), loginAttemptFailed (already tried), or oauthCodeHandled (stale code cleared)
      if (processingCode.current || loginAttemptFailed.current || oauthCodeHandled.current) {
        return; // Already processing, failed, or handled - don't process again
      }

      // If already logged in, the OAuth code is stale - just clear it from URL
      // This can happen when user navigates to a new community while having an old code in URL
      if (isLoggedIn) {
        console.log('Already logged in, clearing stale OAuth code from URL');
        oauthCodeHandled.current = true;
        history.push('/');
        return;
      }

      // Check if we have the PKCE state in localStorage - if not, the code is stale
      // (e.g., user cleared localStorage or is returning to an old URL)
      const pkceState = localStorage.getItem('pkce_state');
      if (!pkceState) {
        // No PKCE state - clear the stale code from URL and start fresh OAuth flow
        console.warn('No PKCE state in localStorage - starting fresh OAuth flow');
        // Mark as handled so we don't loop (oauthCode is from a ref and won't change)
        oauthCodeHandled.current = true;
        history.push('/');
        // Don't set loginAttemptFailed - let it redirect to login
        return;
      }

      processingCode.current = true;
      setIsLoggingIn(true);

      // Pass both code and state captured at mount time to avoid URL race conditions
      auth.login(oauthCode, oauthState)
        .then((data) => {
          // Mark as handled to prevent re-processing (oauthCode from ref won't change)
          oauthCodeHandled.current = true;
          processingCode.current = false;
          loginSuccess(data);
          history.push('/');
          setIsLoggingIn(false);
        })
        .catch((error) => {
          // Keep processingCode true to prevent any retry attempts
          // processingCode.current = false; // DON'T reset - prevents retry loop
          loginAttemptFailed.current = true;
          console.error('OAuth login failed:', error);
          // Remove the code from URL but DON'T redirect to login again
          // This prevents infinite loop
          history.push('/');
          logout();
          setIsLoggingIn(false);
        });
    } else {
      // No code - redirect to OAuth login (but only if we haven't failed before)
      if (loginAttemptFailed.current) {
        // A previous login attempt failed, don't keep trying
        // The user will see the app as logged out / anonymous
        return;
      }
      // Prevent multiple preLogin calls (preLogin is async and the effect might re-run)
      if (preLoginInProgress.current) {
        return;
      }
      preLoginInProgress.current = true;
      setIsLoggingIn(true);
      auth.preLogin();
    }
  }, [isAuthed, isPublic, allowLogin, platform, isLoggedIn, isLoggingIn, hasOAuthCode, oauthCode, oauthState, loginSuccess, logout, setIsLoggingIn, history]);

  return {
    isAuthed,
    isLoggedIn,
    isLoggingIn,
    logout,
  };
};

export default useAppAuth;
